/**
 * ComptabiliteDossier — Détail d'un dossier mensuel : liste des fichiers,
 * upload manuel, envoi cabinet, download.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Upload, Trash2, Send, Download, Link2, RefreshCw, FileText, FileX2, Edit3, Sparkles } from "lucide-react";
import { toast } from "sonner";

const CAT_LABEL = { sales: "Ventes", purchases: "Achats", bank: "Relevés bancaires",
                      sepa: "Remises SEPA", payroll: "Bulletins de paie", other: "Autre" };
const CAT_COLOR = { sales: "bg-emerald-50 text-emerald-700 border-emerald-200",
                      purchases: "bg-orange-50 text-orange-700 border-orange-200",
                      bank: "bg-blue-50 text-blue-700 border-blue-200",
                      sepa: "bg-violet-50 text-violet-700 border-violet-200",
                      payroll: "bg-amber-50 text-amber-700 border-amber-200",
                      other: "bg-slate-50 text-slate-600 border-slate-200" };

export default function ComptabiliteDossier() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("accounting.write") || hasPermission("admin");
  const [folder, setFolder] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({ category: "other", description: "", file: null });
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgText, setMsgText] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [docFilter, setDocFilter] = useState("all"); // all | invoices | credit_notes

  // Helper: un fichier est un avoir si filename commence par "AV-" ou source_id renvoie kind=credit_note
  const isCreditNote = (f) => (f.filename || "").toUpperCase().startsWith("AV-");
  const isInvoice = (f) => (f.filename || "").toUpperCase().startsWith("F-");

  const load = async () => {
    try { const r = await api.get(`/accounting/folders/${id}`); setFolder(r.data); setMsgText(r.data.message || ""); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const upload = async () => {
    if (!uploadForm.file) { toast.error("Fichier requis"); return; }
    const fd = new FormData();
    fd.append("category", uploadForm.category);
    if (uploadForm.description) fd.append("description", uploadForm.description);
    fd.append("file", uploadForm.file);
    try {
      await api.post(`/accounting/folders/${id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document ajouté"); setUploadOpen(false);
      setUploadForm({ category: "other", description: "", file: null }); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const removeFile = async (fid) => {
    if (!window.confirm("Retirer ce fichier du dossier ?")) return;
    try { await api.delete(`/accounting/folders/${id}/files/${fid}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const sync = async () => {
    try { const r = await api.post(`/accounting/folders/${id}/sync`);
      toast.success(`${r.data.total} document(s) collecté(s)`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const send = async () => {
    if (!window.confirm("Envoyer à l'expert-comptable ?")) return;
    try { const r = await api.post(`/accounting/folders/${id}/send`, {});
      toast.success(`Envoyé à ${r.data.sent_to}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const generateLink = async () => {
    try { const r = await api.post(`/accounting/folders/${id}/generate-link`);
      setLinkUrl(r.data.url); toast.success("Lien généré (valable 30 jours)"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveMessage = async () => {
    try { await api.put(`/accounting/folders/${id}/message`, { message: msgText });
      toast.success("Message enregistré"); setMsgOpen(false); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!folder) return <div className="p-6">Chargement...</div>;
  const files = (folder.files || []).filter(f => !f.deleted);

  return (
    <div className="space-y-6" data-testid="ct-folder-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/app/accounting")}>
          <ArrowLeft className="w-4 h-4 mr-1" />Retour
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>{folder.display_name}</h1>
          <div className="flex gap-2 mt-1">
            <Badge variant="outline">{({draft:"Brouillon",ready:"Prêt",sent:"Envoyé",downloaded:"Téléchargé",cancelled:"Annulé"}[folder.status]) || folder.status}</Badge>
            <Badge variant="outline" className="font-mono">Complétude {folder.completeness.score}%</Badge>
            <Badge variant="outline">{files.length} fichier{files.length > 1 ? "s" : ""}</Badge>
          </div>
        </div>
        {canWrite && !["sent", "downloaded"].includes(folder.status) && (
          <Button variant="outline" size="sm" onClick={async () => {
            if (!window.confirm(`Supprimer le dossier ${folder.display_name} ?`)) return;
            try { await api.delete(`/accounting/folders/${id}`);
              toast.success("Dossier supprimé"); navigate("/app/accounting"); }
            catch (e) { toast.error(apiError(e)); }
          }} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="ct-delete-folder-btn">
            <Trash2 className="w-4 h-4 mr-1" />Supprimer
          </Button>
        )}
        {["sent", "downloaded"].includes(folder.status) && (
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="bg-slate-50 text-slate-500 text-xs">🔒 Dossier verrouillé (envoyé)</Badge>
            {(hasPermission("admin") || hasPermission("owner")) && (
              <Button variant="outline" size="sm" onClick={async () => {
                const reason = window.prompt("Raison du déblocage (10 caractères min, log RGPD) :");
                if (!reason || reason.length < 10) { toast.error("Raison ≥ 10 caractères requise"); return; }
                try { await api.post(`/accounting/folders/${id}/unlock`, { reason });
                  toast.success("Dossier débloqué (retour ready)"); load(); }
                catch (e) { toast.error(apiError(e)); }
              }} className="text-amber-700 border-amber-300 hover:bg-amber-50 h-7 text-xs" data-testid="ct-unlock-btn">
                🔓 Débloquer (admin)
              </Button>
            )}
          </div>
        )}
      </div>

      <Card><CardContent className="p-4">
        <Progress value={folder.completeness.score} className="h-2 mb-2" />
        {/* Compteurs par catégorie (parlant pour dirigeant) */}
        <div className="flex flex-wrap gap-2 mb-3" data-testid="ct-cat-counters">
          {Object.entries(folder.completeness.by_category || {}).map(([cat, count]) => {
            const expected = folder.completeness.expected?.includes(cat);
            if (!expected && count === 0) return null;
            const isMissing = expected && count === 0;
            return (
              <Badge key={cat} variant="outline" className={`font-mono text-xs ${isMissing ? "bg-red-50 text-red-700 border-red-200" : count > 0 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500"}`}>
                {CAT_LABEL[cat]} : <span className="ml-1 font-bold">{count}</span>
                {isMissing && <span className="ml-1">⚠</span>}
              </Badge>
            );
          })}
        </div>
        {folder.completeness.missing.length > 0 && (
          <p className="text-xs text-red-600">Catégories manquantes : {folder.completeness.missing.map(c => CAT_LABEL[c]).join(", ")}</p>
        )}
        {folder.ai_report && folder.ai_report.ai_analysis && (
          <div className="mt-3 p-3 bg-amber-50 border-l-4 border-amber-500 rounded" data-testid="ct-ai-report">
            <p className="text-xs font-semibold text-amber-800 mb-1">🤖 Coach IA — Points d'attention</p>
            <pre className="text-xs text-amber-900 whitespace-pre-wrap font-sans leading-relaxed">{folder.ai_report.ai_analysis}</pre>
            {folder.ai_report.kpi && (
              <p className="text-[10px] text-amber-700 mt-2 font-mono">
                CA TTC {folder.ai_report.kpi.ca_ttc?.toFixed(2)} € · TVA à payer {folder.ai_report.kpi.tva_a_payer?.toFixed(2)} € · Variation {folder.ai_report.kpi.variation_ca_pct?.toFixed(1)}%
              </p>
            )}
          </div>
        )}
        <div className="flex flex-wrap gap-2 mt-3">
          {canWrite && (
            <>
              <Button size="sm" onClick={() => setUploadOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="ct-upload-btn">
                <Upload className="w-4 h-4 mr-1" />Ajouter document
              </Button>
              <Button size="sm" variant="outline" onClick={sync} data-testid="ct-sync-btn">
                <RefreshCw className="w-4 h-4 mr-1" />Synchroniser
              </Button>
              <Button size="sm" variant="outline" onClick={async () => {
                try {
                  await api.post(`/accounting/folders/${id}/insights-report`);
                  toast.info("Analyse Coach IA lancée...");
                  // Polling toutes les 2s pendant max ~60s
                  let attempts = 0;
                  const poll = async () => {
                    attempts += 1;
                    try {
                      const g = await api.get(`/accounting/folders/${id}/insights-report`);
                      const s = g.data?.status;
                      if (s === "done" && g.data?.report) {
                        setFolder(prev => ({ ...prev, ai_report: g.data.report }));
                        toast.success("Rapport Coach IA prêt");
                        return;
                      }
                      if (s === "error") {
                        toast.error("Analyse IA en erreur — réessaye plus tard");
                        return;
                      }
                      if (attempts >= 30) {
                        toast.error("Analyse IA trop lente — annulée");
                        return;
                      }
                      setTimeout(poll, 2000);
                    } catch (e) { toast.error(apiError(e)); }
                  };
                  setTimeout(poll, 1500);
                } catch (e) { toast.error(apiError(e)); }
              }} data-testid="ct-ai-btn" className="border-amber-300 text-amber-700 hover:bg-amber-50">
                <Sparkles className="w-4 h-4 mr-1" />Coach IA
              </Button>
              <Button size="sm" variant="outline" onClick={() => setMsgOpen(true)}>
                <Edit3 className="w-4 h-4 mr-1" />Message custom
              </Button>
              <Button size="sm" variant="outline" onClick={generateLink} data-testid="ct-link-btn">
                <Link2 className="w-4 h-4 mr-1" />Générer lien
              </Button>
              <Button size="sm" onClick={send} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="ct-send-btn">
                <Send className="w-4 h-4 mr-1" />Envoyer au cabinet
              </Button>
            </>
          )}
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/accounting/folders/${id}/download`} target="_blank" rel="noreferrer">
            <Button size="sm" variant="ghost"><Download className="w-4 h-4 mr-1" />ZIP local</Button>
          </a>
        </div>
        {linkUrl && (
          <div className="mt-3 p-3 bg-blue-50 rounded-md border border-blue-200">
            <p className="text-xs text-slate-600 mb-1">Lien magique cabinet (30 j) :</p>
            <input id="ct-magic-link-url" name="ct-magic-link-url"
              value={linkUrl} readOnly className="w-full font-mono text-xs bg-white px-2 py-1 border rounded" onClick={(e) => e.target.select()} />
          </div>
        )}
      </CardContent></Card>

      {/* Filtre Ventes : toutes / factures / avoirs */}
      {(files || []).some(f => f.category === "sales") && (
        <div className="flex items-center gap-3 flex-wrap" data-testid="ct-doc-filter-bar">
          <span className="text-xs text-slate-500 uppercase tracking-wide">Filtrer Ventes :</span>
          <Tabs value={docFilter} onValueChange={setDocFilter}>
            <TabsList data-testid="ct-doc-filter-tabs">
              <TabsTrigger value="all" data-testid="filter-all">
                Tout ({files.filter(f => f.category === "sales").length})
              </TabsTrigger>
              <TabsTrigger value="invoices" data-testid="filter-invoices">
                <FileText className="w-3 h-3 mr-1" />
                Factures ({files.filter(f => f.category === "sales" && isInvoice(f)).length})
              </TabsTrigger>
              <TabsTrigger value="credit_notes" data-testid="filter-credit-notes">
                <FileX2 className="w-3 h-3 mr-1" />
                Avoirs ({files.filter(f => f.category === "sales" && isCreditNote(f)).length})
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      )}

      {/* Groupement par catégorie */}
      {["sales", "purchases", "bank", "sepa", "payroll", "other"].map(cat => {
        let catFiles = files.filter(f => f.category === cat);
        // Applique le filtre uniquement sur la catégorie Ventes
        if (cat === "sales" && docFilter !== "all") {
          catFiles = catFiles.filter(f => docFilter === "credit_notes" ? isCreditNote(f) : isInvoice(f));
        }
        if (catFiles.length === 0) return null;
        return (
          <Card key={cat}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Badge variant="outline" className={CAT_COLOR[cat]}>{CAT_LABEL[cat]}</Badge>
                <span className="text-slate-400">({catFiles.length})</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0"><div className="divide-y">
              {catFiles.map(f => {
                const avoir = isCreditNote(f);
                return (
                <div key={f.id} className="p-3 flex items-center gap-3 hover:bg-slate-50" data-testid={`ct-file-${f.id}`}>
                  {avoir ? <FileX2 className="w-4 h-4 text-red-500" /> : <FileText className="w-4 h-4 text-slate-400" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-medium truncate">{f.filename}</div>
                      {avoir && <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-[10px] px-1.5 py-0">AVOIR</Badge>}
                    </div>
                    <div className="text-xs text-slate-500 truncate">{f.description || f.source} · {new Date(f.added_at).toLocaleDateString("fr-FR")}</div>
                  </div>
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/accounting/folders/${id}/files/${f.id}/preview`} target="_blank" rel="noreferrer" className="text-blue-600 text-xs hover:underline">
                    Aperçu
                  </a>
                  {canWrite && (
                    <Button size="sm" variant="ghost" onClick={() => removeFile(f.id)} className="text-red-600 h-7">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
                );
              })}
            </div></CardContent>
          </Card>
        );
      })}

      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent><DialogHeader><DialogTitle>Ajouter un document</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Catégorie</Label>
              <Select value={uploadForm.category} onValueChange={(v) => setUploadForm(p => ({ ...p, category: v }))}>
                <SelectTrigger data-testid="ct-upload-cat"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(CAT_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Description</Label>
              <Input value={uploadForm.description} onChange={(e) => setUploadForm(p => ({ ...p, description: e.target.value }))} />
            </div>
            <div><Label>Fichier (PDF/Image, max 25 Mo)</Label>
              <Input type="file" onChange={(e) => setUploadForm(p => ({ ...p, file: e.target.files?.[0] || null }))} data-testid="ct-upload-file" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>Annuler</Button>
            <Button onClick={upload} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="ct-upload-save">
              <Upload className="w-4 h-4 mr-1.5" />Uploader
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={msgOpen} onOpenChange={setMsgOpen}>
        <DialogContent><DialogHeader><DialogTitle>Message custom pour l'expert</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          <Textarea rows={8} value={msgText} onChange={(e) => setMsgText(e.target.value)}
            placeholder="Bonjour,&#10;&#10;Veuillez trouver ci-joint le dossier..." />
          <p className="text-xs text-slate-500">Tokens : <code>{"{period_label}"}</code>, <code>{"{tenant_name}"}</code></p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMsgOpen(false)}>Annuler</Button>
            <Button onClick={saveMessage} className="bg-blue-600 hover:bg-blue-700 text-white">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
